import { brand } from "shared-lib/helpers/envFunctions";

import { getRecipeImageURL } from "@/lib/helpers/backendFunctions";
import { getPlaintextDescription } from "@/lib/helpers/recipeFunctions";

const buildContent = (recipe: Recipe) => {
  const content = [
    {
      image: "logo",
      fit: [150, 48],
      style: "logoImg",
    },
    {
      text: recipe.category?.name?.toUpperCase(),
      style: "category",
    },
    {
      text: recipe.title,
      style: "header",
    },
    {
      table: {
        headerRows: 1,
        widths: ["100%"],
        body: [[""], [""]],
      },
      layout: "headerLineOnly",
    },
    {
      layout: "noBorders",
      table: {
        headerRows: 1,
        widths: ["*", 270],
        body: [
          [
            {
              ul: [
                {
                  columns: [
                    {
                      width: "auto",
                      text: "Time:",
                      bold: true,
                    },
                    {
                      width: "*",
                      text: recipe.time,
                    },
                  ],
                  columnGap: 10,
                  margin: [0, 0, 0, 15],
                },
                {
                  columns: [
                    {
                      width: "auto",
                      text: "Yield:",
                      bold: true,
                    },
                    {
                      width: "*",
                      text: recipe.yield,
                    },
                  ],
                  columnGap: 10,
                },
              ],
              style: "noMarkerList",
              margin: [0, 15, 0, 0],
            },
            {
              image: "recipeImage",
              style: "recipeImg",
              fit: [270, 177],
              margin: [0, 15, 10, 10],
            },
          ],
        ],
      },
    },
    {
      text: getPlaintextDescription(recipe),
      style: "description",
    },
    { text: "Ingredients", style: "subheader" },
    {
      ul: recipe.ingredients?.map(({ value }) => {
        return {
          text: value,
          style: "listItem",
        };
      }),
    },
    { text: "\nDirections", style: "subheader" },
    {
      ol: recipe.directions?.map(({ value }, index) => {
        return {
          ul: [
            { text: `Step ${index + 1}`, bold: true, style: "listItem" },
            { text: value, style: "listItem" },
          ],
          style: "noMarkerList",
        };
      }),
      margin: [-25, 0, 0, 0],
    },
  ] as any[];
  const styles = {
    logoImg: {
      alignment: "center",
      margin: [0, -20, 0, 20],
    },
    category: {
      fontSize: 8.25,
      characterSpacing: 1,
      font: "regular",
      lineHeight: 1.333,
      margin: [0, 0, 0, 5],
    },
    header: {
      fontSize: 18,
      lineHeight: 1.333,
      margin: [0, 0, 0, 0],
    },
    recipeImg: {
      alignment: "right",
    },
    noMarkerList: {
      markerColor: "white",
    },
    listItem: {
      lineHeight: 1.333,
    },
    description: {
      lineHeight: 1.333,
      margin: [0, 10, 0, 10],
    },
    subheader: {
      fontSize: 18,
      font: "regular",
      lineHeight: 1.333,
      margin: [0, 10, 0, 10],
      bold: true,
    },
  };
  return { content, styles };
};

const LOGOS = {
  CC: "https://images.ctfassets.net/7t85a6f8wt2m/4Pd3D6JTGtBnWVc6z1LImT/d3a4ee8df0b57a901b007c1d624df388/logo-cc.png",
  CP: "https://images.ctfassets.net/7t85a6f8wt2m/17UoDC4b22KWcygkymgnCk/a3784d5ab0e34529c8d93ac0ebcebb3c/logo-cp.png",
  HLC: "https://images.ctfassets.net/7t85a6f8wt2m/7x6OG4v7Hea3xdtyhxz2CN/fcfaf8b1012784c3ea1b9f3988962891/logo-hlc.png",
  EE: "https://images.ctfassets.net/7t85a6f8wt2m/60QlfO6UDiUmt4in32ZB2n/1d37b24a7243b27a6e9c3c6518242812/logo-ee.jpg",
  LC: "https://images.ctfassets.net/7t85a6f8wt2m/5QN6o66wQNlf50xLWNnHlK/4a7455bb186f95784713ff71e1a7c2d8/logo-lc.png",
  MC: "https://images.ctfassets.net/7t85a6f8wt2m/1mi2G1EBgnQZyz3nqP0yVm/306aef0fdee9a01db774a44214b7ce64/logo-mc.png",
  RD: "https://images.ctfassets.net/7t85a6f8wt2m/5y4sYV7OpryNC9371sqBeJ/3a66e844c57921f690ec7261146e187d/logo-rd.png",
  RR: "https://images.ctfassets.net/7t85a6f8wt2m/7iglVVVasTNkoHsr4EwOPi/3515cd2818ed244310abfb60b9a18377/logo-rr.png",
  RS: "https://images.ctfassets.net/7t85a6f8wt2m/6rcMobmCMV50zIhxnRsovy/0161bec675c33adf5425a1cefc550713/logo-rs.png",
  FK: "https://images.ctfassets.net/7t85a6f8wt2m/1k2gjpPrsI4g2FltmXeTJ6/c95f3da46b4eb26964b28bff1774242d/logo-fk.png",
  HR: "https://images.ctfassets.net/7t85a6f8wt2m/1Liwp3wgRBzYihGM7z7VOT/8a929433058d02509465273915115a51/logo-hr.png",
  BR: "https://images.ctfassets.net/7t85a6f8wt2m/5PWO4iCXyheyEJBoUwrW6v/00512cae62feea0923059621e6afa10c/logo-br.png",
};

export const preparePdf = async (recipe: Recipe) => {
  const content = buildContent(recipe);
  const [url] = getRecipeImageURL(recipe, 580, 880).split("?");
  return {
    ...content,
    defaultStyle: {
      font: "serif",
      fontSize: 10,
    },
    pageSize: "A4",
    pageMargins: [40, 40, 40, 40],
    images: {
      recipeImage: { url },
      logo: LOGOS[brand.adPrefix as keyof typeof LOGOS],
    },
  };
};

const fonts = {
  serif: {
    normal: "https://fonts.cdnfonts.com/s/12231/Merriweather-Regular.woff",
    italics: "https://fonts.cdnfonts.com/s/12231/Merriweather-Italic.woff",
    bold: "https://fonts.cdnfonts.com/s/12231/Merriweather-Bold.woff",
    bolditalics:
      "https://fonts.cdnfonts.com/s/12231/Merriweather-BoldItalic.woff",
  },
  regular: {
    normal: "https://fonts.cdnfonts.com/s/14882/Lato-Regular.woff",
    italics: "https://fonts.cdnfonts.com/s/14882/Lato-Italic.woff",
    bold: "https://fonts.cdnfonts.com/s/14882/Lato-Bold.woff",
    bolditalics: "https://fonts.cdnfonts.com/s/14882/Lato-BoldItalic.woff",
  },
};

export async function printRecipe(recipe: Recipe) {
  const pdfMake = await import("pdfmake/build/pdfmake");
  const params = `popup=yes,scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=0,height=0,width=0,height=0,top=0,left=0`;
  const win = window.open("", "_blank", params);
  const definition = await preparePdf(recipe);
  return pdfMake.createPdf(<any>definition, {}, fonts).print({}, win);
}

export async function downloadRecipe(recipe: Recipe) {
  const pdfMake = await import("pdfmake/build/pdfmake");
  const definition = await preparePdf(recipe);
  return pdfMake
    .createPdf(<any>definition, {}, fonts)
    .download((recipe.title ?? "").replace(/ /g, "_"));
}
